import { PageHeaderWrapper, Text, Title } from "@clipboard-health/ui-components";
import { Box, DialogContent, Stack } from "@mui/material";
import { DialogFooter } from "@src/appV2/redesign/components/DialogFooter";
import { LoadingButton } from "@src/appV2/redesign/components/LoadingButton";
import { PageHeaderTopRow } from "@src/appV2/redesign/components/PageHeaderTopRow";
import { TextButton } from "@src/appV2/redesign/components/TextButton";

export interface BadgeVerificationConfirmContentProps {
  photoPreviewUrl: string;
  isSubmitting: boolean;
  onBack: () => void;
  onSubmit: () => void;
  onRetakePhoto: () => void;
}

export function BadgeVerificationConfirmContent(props: BadgeVerificationConfirmContentProps) {
  const { photoPreviewUrl, isSubmitting, onBack, onSubmit, onRetakePhoto } = props;

  return (
    <>
      <PageHeaderWrapper variant="tertiary">
        <PageHeaderTopRow
          hideBackButton={isSubmitting}
          // To still keep the height of the header when back button is hidden
          sx={{ minHeight: 72 }}
          onClickBack={onBack}
        />
      </PageHeaderWrapper>

      <DialogContent
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Stack alignItems="center" textAlign="center">
          <Title variant="h4" component="h1">
            Confirm badge submission
          </Title>

          <Text variant="body1" color={(theme) => theme.palette.text.secondary} marginTop={6}>
            You will clock in right after you submit the picture of your badge
          </Text>

          <Box
            sx={(theme) => ({
              width: "100%",
              maxWidth: 300,
              aspectRatio: "3/4",
              borderRadius: theme.shape.borderRadius,
              overflow: "hidden",
              backgroundColor: theme.palette.grey[200],
              marginTop: 4,
            })}
          >
            <Box
              component="img"
              src={photoPreviewUrl}
              alt="Badge photo preview"
              sx={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
              }}
            />
          </Box>

          <Text variant="body2" color={(theme) => theme.palette.text.secondary} marginTop={4}>
            This photo will be shared with the workplace to ensure compliance to their badge policy.
            Your score will be updated after your submission is reviewed.
          </Text>
        </Stack>
      </DialogContent>

      <DialogFooter orientation="vertical">
        <LoadingButton
          fullWidth
          variant="contained"
          size="large"
          isLoading={isSubmitting}
          // Test ID needed because LoadingButton changes role to "progressbar" when loading
          data-testid="badge-verification-submit-button"
          onClick={onSubmit}
        >
          Submit photo and clock in
        </LoadingButton>
        <TextButton size="large" disabled={isSubmitting} onClick={onRetakePhoto}>
          Take a new photo
        </TextButton>
      </DialogFooter>
    </>
  );
}
